import {Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule, Validators} from '@angular/forms';
import {VwuiAngularModule, VwuiModalConfig, VwuiModalRef} from '@bvgo-ict/vwui-angular';
import {NgSelectModule} from '@ng-select/ng-select';
import {TranslatePipe} from '@ngx-translate/core';
import {FormControl, FormGroup} from '@ng-stack/forms';

import {ProjectAnalysisDefinitionService, ProjectTypeService} from '../../services';
import {ProjectAnalysisVersion, ProjectType} from '../../models/project-analysis';

export interface CopyProjectAnalysisModalData {
    targetProjectType: ProjectType;
}

export interface CopyProjectAnalysisModalResult {
    sourceProjectTypeId: number;
    sourceVersionId: number;
}

@Component({
    selector: 'app-copy-project-analysis-modal',
    templateUrl: './copy-project-analysis-modal.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, ReactiveFormsModule, VwuiAngularModule, NgSelectModule, TranslatePipe]
})
export class CopyProjectAnalysisModalComponent implements OnInit {
    public readonly modalRef = inject(VwuiModalRef);
    public readonly modalConfig = inject<VwuiModalConfig<CopyProjectAnalysisModalData>>(VwuiModalConfig);
    private readonly projectTypeService = inject(ProjectTypeService);
    private readonly definitionService = inject(ProjectAnalysisDefinitionService);
    private readonly destroyRef = inject(DestroyRef);

    public readonly form = new FormGroup({
        sourceProjectTypeId: new FormControl<number | null>(null, Validators.required),
        sourceVersionId: new FormControl<number | null>(null, Validators.required)
    });

    public readonly availableProjectTypes = signal<ProjectType[]>([]);
    public readonly availableVersions = signal<ProjectAnalysisVersion[]>([]);
    public readonly isLoadingVersions = signal(false);

    private readonly targetProjectTypeId: number;

    constructor() {
        this.targetProjectTypeId = this.modalConfig.data.targetProjectType.id;
    }

    ngOnInit(): void {
        this.projectTypeService.list().subscribe((projectTypes) => {
            this.availableProjectTypes.set(
                projectTypes.filter((pt) => pt.id !== this.targetProjectTypeId)
            );
        });

        this.form.controls.sourceProjectTypeId.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((projectTypeId) => {
                this.availableVersions.set([]);
                this.form.controls.sourceVersionId.reset(null);

                if (projectTypeId != null) {
                    this.form.controls.sourceVersionId.enable();
                    this.loadVersions(projectTypeId);
                } else {
                    this.form.controls.sourceVersionId.disable();
                }
            });
    }

    public submit(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        this.modalRef.close({
            sourceProjectTypeId: this.form.value.sourceProjectTypeId,
            sourceVersionId: this.form.value.sourceVersionId
        });
    }

    private loadVersions(projectTypeId: number): void {
        this.isLoadingVersions.set(true);
        this.definitionService.getVersions(projectTypeId).subscribe({
            next: (versions) => {
                this.availableVersions.set([...versions].sort((a, b) => b.versionId - a.versionId));
                this.isLoadingVersions.set(false);
            },
            error: () => {
                this.availableVersions.set([]);
                this.isLoadingVersions.set(false);
            }
        });
    }
}
